import { infiniteQueryFetchNextPage } from '@repo/sdk'
import { zV1GroupsListData } from '@repo/sdk/zod'
import { Button } from '@repo/ui/button'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListToolbar } from '@repo/ui/components/list-toolbar'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { getErrorCode, raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
	createFileRoute,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { groupsListQueryOptions } from '@/modules/groups/query/query-options'
import { makeQueryKeys } from '@/query/query-keys'
import { GroupsEmpty } from '@/routes/(private)/(shell)/groups/-components/groups-empty'
import { GroupsTable } from '@/routes/(private)/(shell)/groups/-components/groups-table'
import { NewGroupDialog } from '@/routes/(private)/(shell)/groups/-components/new-group-dialog'
import { usePageSize } from '@/stores/use-page-size'

const searchSchema = z.object({
	search: z.string().trim().default(''),
	// «I miei gruppi»: the server narrows the list to the caller's groups.
	mine: z.boolean().default(false)
})

export const Route = createFileRoute('/(private)/(shell)/groups/')({
	component: Groups,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

/**
 * The groups the caller can see (OPSSTD-10380): search by name and «I miei
 * gruppi». A member sees the public groups and the private ones they are in,
 * an administrator every group; each row says whether the caller may change
 * it (`canManage`), and anyone may create one. The list shares `admin/people`'s
 * paging: the endpoint's cursor, consumed as an infinite query, and a cursor
 * the server no longer honours answered by starting again from the first page.
 *
 * The creation dialog is mounted once and driven by `open`; the empty state
 * for a search opens it with the searched name already typed.
 */
function Groups() {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((state) => state.pageSize)
	const [creating, setCreating] = useState<{
		open: boolean
		name: string
	}>({ open: false, name: '' })

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		q: params.search || undefined,
		member: params.mine
	}
	const groups = useInfiniteQuery(groupsListQueryOptions(listQuery))
	const rows = groups.data?.groups ?? []
	const cursorExpired = getErrorCode(groups.error) === 'cursor_invalid'

	/** `cursor_invalid` is routine: drop the pages held and ask again. */
	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(groups)

		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.groups.index(listQuery)
			})
		}
	}

	function setMine(mine: boolean) {
		navigate({ to: '.', search: (prev) => ({ ...prev, mine }) })
	}

	function openCreate(name: string) {
		setCreating({ open: true, name })
	}

	return (
		<PageLayout
			bar={
				<ListToolbar
					actions={
						<Button onClick={() => openCreate('')} type='button'>
							<Plus data-icon='inline-start' />
							{t('groups:new.action')}
						</Button>
					}
					search={
						<ListInputSearch
							placeholder={t('groups:search_placeholder')}
						/>
					}
				>
					<Button
						aria-pressed={params.mine}
						className='shadow-none'
						onClick={() => setMine(!params.mine)}
						size='sm'
						type='button'
						variant={params.mine ? 'default' : 'outline'}
					>
						{t('groups:mine')}
					</Button>
				</ListToolbar>
			}
			breadcrumb={<QueryBreadcrumbs />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 md:px-6'>
				<GroupsTable
					data={rows}
					loadMore={{
						hasMore: groups.hasNextPage,
						loading: groups.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					state={{
						empty: (
							<GroupsEmpty
								mine={params.mine}
								onCreate={openCreate}
								onShowAll={() => setMine(false)}
								search={params.search}
							/>
						),
						error: groups.isError && !cursorExpired,
						loading: groups.isPending || groups.isPlaceholderData
					}}
					total={groups.data?.total ?? 0}
				/>
			</div>

			<NewGroupDialog
				initialName={creating.name}
				onOpenChange={(open) =>
					setCreating((current) => ({ ...current, open }))
				}
				open={creating.open}
			/>
		</PageLayout>
	)
}

/** The endpoint's own ceiling on a page, read off the contract. */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(zV1GroupsListData.shape.query.unwrap().shape.limit) ??
	raise('list-groups lost its page-size ceiling')
